import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { DataGridPremium } from '@mui/x-data-grid-premium'
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchLightweightUsersV4,
  selectLightweightUsersV4ByService,
  selectLightweightUsersV4StatusByService,
} from 'src/v3/manage/manage.slice'
import {
  extractLicenseTypes,
  filterUsers,
  transformUsersToRows,
} from 'src/v3/manage/groups/utils/user-transforms'
import type { UserRow } from 'src/v3/manage/groups/utils/user-transforms'
import { dataGridSx, largeModalSx } from 'src/v3/manage/styles'
import {
  addSessionInvites,
  fetchSessionInvites,
  removeSessionInvites,
} from '../utils/api'
import { LICENSE_TYPE_COLORS } from '../utils/constants'
import { STATUS_LABELS, useStatusChipColor } from '../utils/invite-status'
import { formatSessionMeta } from '../utils/session-time'
import type {
  EventInviteItem,
  EventInviteStatus,
  SessionOption,
} from '../types'

// Server limit per request on both bulk invite routes.
let MAX_BULK_INVITE_USERS = 5000

type InviteRow = UserRow & { inviteStatus: EventInviteStatus | null }

// Only users with no invite or a pending one can be toggled. Any other status
// is a learner response (or attendance) that managers change on the Attendees tab.
let isSelectable = (row: InviteRow) =>
  row.inviteStatus == null || row.inviteStatus === 'invited'

let lockedTooltip = (status: EventInviteStatus) =>
  status === 'declined'
    ? 'Declined. Change status on the Attendees tab to re-invite.'
    : 'Change status on the Attendees tab.'

let saveErrorMessage = (error: any): string => {
  let status = error?.response?.status
  let data = error?.response?.data
  // 400/409 bodies are plain-text messages meant for the manager.
  return (status === 400 || status === 409) && typeof data === 'string' && data
    ? data
    : 'Failed to update invites'
}

// ─── Grid ─────────────────────────────────────────────────────────────────────

type InviteUsersGridProps = {
  userRows: UserRow[]
  invites: EventInviteItem[]
  saving: boolean
  onSave: (toAdd: string[], toRemove: string[]) => void
  onCancel: () => void
}

// Remounted (via key) on every invite load, so the selection is initialized
// from the invites it was given rather than synced in an effect.
let InviteUsersGrid = ({
  userRows,
  invites,
  saving,
  onSave,
  onCancel,
}: InviteUsersGridProps) => {
  let getStatusColor = useStatusChipColor()
  let [search, setSearch] = useState('')
  let [licenseFilter, setLicenseFilter] = useState('')

  // Invite rows aren't unique per user (a bulk add can race a self-register).
  // A responded row wins over an 'invited' one: the server refuses to remove a
  // user if any of their rows has responded, and would 409 the whole batch.
  let statusByUser = useMemo(
    () =>
      invites.reduce((acc, i) => {
        let existing = acc.get(i.userID)
        if (existing == null || existing === 'invited')
          acc.set(i.userID, i.status)
        return acc
      }, new Map<string, EventInviteStatus>()),
    [invites],
  )

  // Users with an invite first, so the current roster is visible on open.
  let rows = useMemo(
    () =>
      userRows
        .map((u): InviteRow => ({
          ...u,
          inviteStatus: statusByUser.get(u.id) ?? null,
        }))
        .toSorted(
          (a, b) =>
            Number(b.inviteStatus != null) - Number(a.inviteStatus != null) ||
            a.name.localeCompare(b.name),
        ),
    [userRows, statusByUser],
  )

  let initialInvited = useMemo(
    () =>
      new Set(
        rows.filter((r) => r.inviteStatus === 'invited').map((r) => r.id),
      ),
    [rows],
  )
  let lockedIDs = useMemo(
    () => new Set(rows.filter((r) => !isSelectable(r)).map((r) => r.id)),
    [rows],
  )

  let [selected, setSelected] = useState<ReadonlySet<string>>(
    () => initialInvited,
  )

  let licenseTypes = useMemo(() => extractLicenseTypes(userRows), [userRows])

  // filterUsers keeps selected users visible regardless of the filters (same
  // as groups), so a selection is never hidden behind a search. It only drops
  // rows, so the InviteRow shape survives.
  let visibleRows = useMemo(
    () =>
      filterUsers(rows, [...selected], licenseFilter, search) as InviteRow[],
    [rows, selected, licenseFilter, search],
  )

  let toAdd = [...selected].filter((id) => !statusByUser.has(id))
  let toRemove = [...initialInvited].filter((id) => !selected.has(id))
  let overLimit =
    toAdd.length > MAX_BULK_INVITE_USERS ||
    toRemove.length > MAX_BULK_INVITE_USERS

  // Locked rows render checked, but only the visible ones go in the model: the
  // grid unselects IDs missing from its rows and fires a change, which would
  // loop against this model if hidden locked IDs were included.
  let selectionModel: GridRowSelectionModel = useMemo(
    () => ({
      type: 'include',
      ids: new Set([
        ...selected,
        ...visibleRows.filter((r) => lockedIDs.has(r.id)).map((r) => r.id),
      ]),
    }),
    [selected, visibleRows, lockedIDs],
  )

  // With isRowSelectable set, select-all always reports an include model.
  let handleSelectionChange = (model: GridRowSelectionModel) => {
    // The save in flight was built from the selection at click time; edits now
    // would show as applied and then be dropped when the dialog closes.
    if (saving) return
    let next = new Set(
      (Array.from(model.ids) as string[]).filter((id) => !lockedIDs.has(id)),
    )
    let unchanged =
      next.size === selected.size && [...next].every((id) => selected.has(id))
    if (!unchanged) setSelected(next)
  }

  let columns: GridColDef<InviteRow>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.5,
        minWidth: 180,
      },
      {
        field: 'primaryEmail',
        headerName: 'Email',
        flex: 2,
        minWidth: 200,
      },
      {
        field: 'licenseTypes',
        headerName: 'License',
        flex: 1.2,
        minWidth: 140,
        valueGetter: (_val, row) => row.licenseTypes.join(', '),
        renderCell: ({ row }: GridRenderCellParams<InviteRow>) =>
          row.licenseTypes.length === 0 ? (
            <Typography sx={{ color: 'rgba(255,255,255,.3)' }}>—</Typography>
          ) : (
            <Box className="flex flex-wrap items-center gap-1 h-full">
              {row.licenseTypes.map((lt) => {
                let licColor = LICENSE_TYPE_COLORS[lt] ?? '#72CCFF'
                return (
                  <Chip
                    key={lt}
                    label={lt}
                    size="small"
                    sx={{
                      bgcolor: `color-mix(in srgb, ${licColor} 16%, transparent)`,
                      color: licColor,
                      border: `1px solid color-mix(in srgb, ${licColor} 40%, transparent)`,
                    }}
                  />
                )
              })}
            </Box>
          ),
      },
      {
        field: 'inviteStatus',
        headerName: 'RSVP Status',
        flex: 1,
        minWidth: 140,
        valueGetter: (_val, row) =>
          row.inviteStatus ? STATUS_LABELS[row.inviteStatus] : '',
        renderCell: ({ row }: GridRenderCellParams<InviteRow>) => {
          if (!row.inviteStatus)
            return (
              <Typography sx={{ color: 'rgba(255,255,255,.3)' }}>—</Typography>
            )
          let color = getStatusColor(row.inviteStatus)
          let chip = (
            <Chip
              label={STATUS_LABELS[row.inviteStatus]}
              size="small"
              sx={{
                bgcolor: `color-mix(in srgb, ${color} 16%, transparent)`,
                color,
                border: `1px solid color-mix(in srgb, ${color} 40%, transparent)`,
              }}
            />
          )
          return isSelectable(row) ? (
            chip
          ) : (
            <Tooltip title={lockedTooltip(row.inviteStatus)}>{chip}</Tooltip>
          )
        },
      },
    ],
    [getStatusColor],
  )

  return (
    <>
      <DialogContent className="flex flex-col gap-4">
        <Box className="flex flex-wrap items-center gap-4 pt-2">
          <TextField
            label="Search users…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1"
            sx={{ minWidth: 200 }}
          />
          <FormControl className="flex-1" sx={{ minWidth: 170 }}>
            <InputLabel id="invite-users-license-label" shrink>
              License
            </InputLabel>
            <Select
              labelId="invite-users-license-label"
              label="License"
              value={licenseFilter}
              onChange={(e) => setLicenseFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>All licenses</em>
              </MenuItem>
              {licenseTypes.map((lt) => (
                <MenuItem key={lt} value={lt}>
                  {lt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box className="flex-1 min-h-0">
          <DataGridPremium
            rows={visibleRows}
            columns={columns}
            checkboxSelection
            disableRowSelectionOnClick
            isRowSelectable={({ row }) => !saving && isSelectable(row)}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={handleSelectionChange}
            // Otherwise the grid unselects rows its column filters hide, which
            // would queue those pending invites for removal unseen.
            keepNonExistentRowsSelected
            // A flat selector: Premium's grouping/aggregation/pivoting would
            // add generated rows the columns and selection logic don't handle.
            disableRowGrouping
            disableAggregation
            disablePivoting
            sx={dataGridSx}
          />
        </Box>
      </DialogContent>

      <DialogActions className="flex justify-between gap-4 px-6 py-4">
        <Box>
          <Typography sx={{ fontWeight: 500 }}>
            Invite {toAdd.length} · Remove {toRemove.length}
          </Typography>
          {overLimit ? (
            <Typography variant="caption" color="error">
              You can invite or remove up to{' '}
              {MAX_BULK_INVITE_USERS.toLocaleString('en-US')} users per save.
            </Typography>
          ) : (
            toAdd.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Invited users will receive an email.
              </Typography>
            )
          )}
        </Box>
        <Box className="flex gap-2">
          <Button onClick={onCancel} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => onSave(toAdd, toRemove)}
            disabled={
              saving ||
              overLimit ||
              (toAdd.length === 0 && toRemove.length === 0)
            }
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </>
  )
}

// ─── Dialog ───────────────────────────────────────────────────────────────────

type Props = {
  open: boolean
  onClose: () => void
  linkedServiceID: string
  session: SessionOption
  onSaved: () => void
}

let InviteUsersDialog = ({
  open,
  onClose,
  linkedServiceID,
  session,
  onSaved,
}: Props) => {
  let dispatch = useAppDispatch()
  let users = useAppSelector(selectLightweightUsersV4ByService(linkedServiceID))
  let usersStatus = useAppSelector(
    selectLightweightUsersV4StatusByService(linkedServiceID),
  )
  let userRows = useMemo(() => transformUsersToRows(users), [users])

  let [invites, setInvites] = useState<EventInviteItem[] | null>(null)
  let [invitesFailed, setInvitesFailed] = useState(false)
  let [loadVersion, setLoadVersion] = useState(0)
  let [saving, setSaving] = useState(false)
  let [error, setError] = useState<string | null>(null)

  // Read through a ref so the open effect below doesn't re-run on every status
  // change; with the status as a dep, a persistent failure would refetch in a loop.
  let usersStatusRef = useRef(usersStatus)
  // eslint-disable-next-line fp/no-mutation
  usersStatusRef.current = usersStatus

  // Fetches on first use and retries a failed load. The users are cached in the
  // store per service, so a 'rejected' status would otherwise stick until a
  // full page reload.
  let loadUsers = useCallback(() => {
    let status = usersStatusRef.current
    if (status === 'uninitialized' || status === 'rejected') {
      dispatch(fetchLightweightUsersV4(linkedServiceID))
    }
  }, [linkedServiceID, dispatch])

  // Only the latest load may write state. Closing the dialog also bumps it, so
  // a reload still in flight (e.g. after a failed remove) can't land after a
  // reopen and remount the grid over the manager's new selection.
  let loadSeqRef = useRef(0)

  let loadInvites = useCallback(() => {
    // eslint-disable-next-line fp/no-mutation
    loadSeqRef.current += 1
    let seq = loadSeqRef.current
    setInvites(null)
    setInvitesFailed(false)
    fetchSessionInvites(session.id)
      .then((result) => {
        if (seq !== loadSeqRef.current) return
        setInvites(result)
        setLoadVersion((v) => v + 1)
      })
      .catch(() => {
        if (seq === loadSeqRef.current) setInvitesFailed(true)
      })
  }, [session.id])

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line fp/no-mutation
      loadSeqRef.current += 1
      return
    }
    setError(null)
    loadUsers()
    loadInvites()
  }, [open, loadUsers, loadInvites])

  let resetLoadedState = () => {
    setInvites(null)
    setInvitesFailed(false)
    setError(null)
  }

  let handleSave = async (toAdd: string[], toRemove: string[]) => {
    setSaving(true)
    setError(null)

    // The server rejects an empty list, so each call only runs when it has users.
    // Add goes first; if it fails, remove is skipped.
    try {
      if (toAdd.length > 0) await addSessionInvites(session.id, toAdd)
    } catch (e: any) {
      setError(saveErrorMessage(e))
      setSaving(false)
      // 400/409 are returned before any write. Anything else (5xx, timeout,
      // dropped connection) can arrive after the rows were inserted and emailed,
      // so refresh from the server. A retry is safe: existing rows are skipped.
      let status = e?.response?.status
      if (status !== 400 && status !== 409) {
        onSaved()
        loadInvites()
      }
      return
    }

    try {
      if (toRemove.length > 0) await removeSessionInvites(session.id, toRemove)
    } catch (e) {
      // Usually a 409: a learner responded while the dialog was open. Any adds
      // are committed, and a lost 5xx response may hide a committed removal, so
      // always refresh the parent and reload the roster from the server (not
      // add's response; a learner can respond between the calls).
      setError(saveErrorMessage(e))
      setSaving(false)
      onSaved()
      loadInvites()
      return
    }

    setSaving(false)
    onSaved()
    onClose()
  }

  let loading =
    invites == null ||
    usersStatus === 'uninitialized' ||
    usersStatus === 'pending'
  let loadFailed = invitesFailed || usersStatus === 'rejected'

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      slotProps={{
        ...largeModalSx,
        // The tabs keep this mounted while closed. Clear the last session's
        // roster once the close animation ends (not on close, which would flash
        // a spinner mid-animation), so the next open can't paint stale data.
        transition: { onExited: resetLoadedState },
      }}
      aria-labelledby="invite-users-dialog-title"
    >
      <DialogTitle id="invite-users-dialog-title">
        Invite users
        <Typography variant="body2" color="text.secondary">
          {session.title} · {formatSessionMeta(session.ts, session.timeZone)}
        </Typography>
      </DialogTitle>

      {error && (
        <Alert severity="error" className="mx-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loadFailed ? (
        <>
          <DialogContent>
            <Typography color="error">Failed to load users.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="inherit">
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                loadUsers()
                loadInvites()
              }}
            >
              Retry
            </Button>
          </DialogActions>
        </>
      ) : loading ? (
        <DialogContent className="flex items-center justify-center">
          <CircularProgress />
        </DialogContent>
      ) : (
        <InviteUsersGrid
          key={loadVersion}
          userRows={userRows}
          invites={invites!}
          saving={saving}
          onSave={handleSave}
          onCancel={onClose}
        />
      )}
    </Dialog>
  )
}

export default InviteUsersDialog
